import {
  WhatsAppTemplateBodyComponentType,
  WhatsAppTemplateButtonComponentType,
  WhatsAppTemplateHeaderComponentType,
  WhatsAppTemplateMessageButtonSubType,
  WhatsAppTemplateMessageTextParameterType,
  type WhatsAppTemplate,
  type WhatsAppTemplateMessageComponent,
} from 'src/api/generated';
import type {
  TemplateComposerDefinition,
  TemplateComposerField,
  TemplateComposerValues,
} from '../types/templates/template-composer.types';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';

/**
 * Narrows provider metadata without assuming a component shape.
 * @param value - Untrusted component metadata.
 * @returns Whether the value is a non-null object rather than an array.
 */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Extracts distinct, contiguous positional fields in transport order.
 * @param text - Approved text with positional placeholders.
 * @param section - Component namespace for draft keys.
 * @returns Field descriptors or undefined for unsupported/malformed positions.
 */
const positionalFields = (
  text: string,
  section: 'header' | 'body'
): TemplateComposerField[] | undefined => {
  const tokens = Array.from(
    text.matchAll(/\{\{([^}]+)\}\}/g),
    (match) => match[1]
  );
  if (tokens.some((token) => !/^[1-9]\d*$/.test(token))) return undefined;
  const positions = [...new Set(tokens.map(Number))].sort((a, b) => a - b);
  if (positions.some((position, index) => position !== index + 1)) {
    return undefined;
  }
  return positions.map((position) => ({
    key: `${section}:${position}`,
    section,
    position,
    context: text,
  }));
};

/**
 * Creates independent empty descriptor collections.
 * @returns A new definition with no editable content.
 */
const emptyDefinition = (): TemplateComposerDefinition => ({
  fields: [],
  header: '',
  body: '',
  footer: '',
  buttons: [],
});

/**
 * Combines component descriptors while preserving the first blocking reason.
 * @param left - Accumulated definition.
 * @param right - Next component definition.
 * @returns Combined immutable descriptor data.
 */
const mergeDefinitions = (
  left: TemplateComposerDefinition,
  right: TemplateComposerDefinition
): TemplateComposerDefinition => ({
  header: right.header || left.header,
  body: right.body || left.body,
  footer: right.footer || left.footer,
  fields: [...left.fields, ...right.fields],
  buttons: [...left.buttons, ...right.buttons],
  unavailableReason: left.unavailableReason ?? right.unavailableReason,
});

/**
 * Recognizes static links and the supported single dynamic suffix.
 * @param text - Approved button label.
 * @param url - Approved URL definition.
 * @param index - Position in the full provider button array.
 * @returns Preview metadata, optional suffix field and any blocking reason.
 */
const describeUrlButton = (
  text: string,
  url: string,
  index: number
): TemplateComposerDefinition => {
  const result = emptyDefinition();
  if (!/^https?:\/\//i.test(url)) {
    result.unavailableReason = 'invalidDefinition';
    return result;
  }
  let fieldKey: string | undefined;
  if (url.includes('{{')) {
    if (!/^[^{]*\{\{1\}\}$/.test(url)) {
      result.unavailableReason = 'unsupportedFormat';
      return result;
    }
    fieldKey = `button:${index}:1`;
    result.fields = [
      {
        key: fieldKey,
        section: 'button',
        position: 1,
        context: url,
        buttonIndex: index,
        buttonLabel: text,
        urlPrefix: url.slice(0, -5),
      },
    ];
  }
  result.buttons = [{ type: 'url', text, destination: url, fieldKey }];
  return result;
};

/**
 * Validates one provider action before exposing its preview or parameters.
 * @param button - Raw provider action.
 * @param index - Position in the full button array.
 * @returns Supported metadata or an explicit unavailable reason.
 */
const describeButton = (
  button: unknown,
  index: number
): TemplateComposerDefinition => {
  const result = emptyDefinition();
  if (
    !isRecord(button) ||
    typeof button.type !== 'string' ||
    typeof button.text !== 'string'
  ) {
    return { ...result, unavailableReason: 'unsupportedFormat' };
  }
  const type = button.type.toLowerCase();
  if (type === 'quick_reply') {
    return { ...result, buttons: [{ type, text: button.text }] };
  }
  if (type === 'phone_number' && typeof button.phone_number === 'string') {
    return {
      ...result,
      buttons: [{ type, text: button.text, destination: button.phone_number }],
    };
  }
  if (type === 'url' && typeof button.url === 'string') {
    return describeUrlButton(button.text, button.url, index);
  }
  return { ...result, unavailableReason: 'unsupportedFormat' };
};

/**
 * Describes approved header/body/footer text without using approval examples.
 * @param component - Provider component metadata.
 * @param type - Validated text component type.
 * @returns Approved text, editable fields and format limitations.
 */
const describeTextComponent = (
  component: Record<string, unknown>,
  type: 'header' | 'body' | 'footer'
): TemplateComposerDefinition => {
  const result = emptyDefinition();
  if (
    type === 'header' &&
    component.format !== undefined &&
    (typeof component.format !== 'string' ||
      component.format.toLowerCase() !== 'text')
  ) {
    return { ...result, unavailableReason: 'mediaHeader' };
  }
  if (typeof component.text !== 'string' || !component.text.trim()) {
    return { ...result, unavailableReason: 'invalidDefinition' };
  }
  result[type] = component.text;
  if (type === 'footer') {
    return {
      ...result,
      unavailableReason: component.text.includes('{{')
        ? 'unsupportedFormat'
        : undefined,
    };
  }
  const fields = positionalFields(component.text, type);
  if (!fields) {
    return {
      ...result,
      unavailableReason: /\{\{[a-z_]/i.test(component.text)
        ? 'namedParameters'
        : 'invalidDefinition',
    };
  }
  return { ...result, fields };
};

/**
 * Routes validated provider component names to supported descriptors.
 * @param component - Provider metadata.
 * @param type - Lowercase component type.
 * @returns Component descriptors or a blocking format reason.
 */
const describeComponent = (
  component: Record<string, unknown>,
  type: string
): TemplateComposerDefinition => {
  if (type === 'header' || type === 'body' || type === 'footer') {
    return describeTextComponent(component, type);
  }
  if (type === 'buttons' && Array.isArray(component.buttons)) {
    return component.buttons
      .map(describeButton)
      .reduce(mergeDefinitions, emptyDefinition());
  }
  return { ...emptyDefinition(), unavailableReason: 'unsupportedFormat' };
};

/**
 * Validates every component before exposing a template as sendable.
 * @param template - Current provider definition and approval state.
 * @returns Editable fields, inert preview metadata and the first blocking reason.
 * @example
 * const definition = describeTemplate(template);
 * const sendable = !definition.unavailableReason;
 */
export const describeTemplate = (
  template: WhatsAppTemplate
): TemplateComposerDefinition => {
  let result = emptyDefinition();
  if (template.status.toUpperCase() !== 'APPROVED') {
    result.unavailableReason = 'notApproved';
  } else if (template.category.toUpperCase() === 'AUTHENTICATION') {
    result.unavailableReason = 'authentication';
  } else if (template.parameter_format?.toUpperCase() === 'NAMED') {
    result.unavailableReason = 'namedParameters';
  }
  const seen = new Set<string>();
  for (const raw of template.components) {
    const component: unknown = raw;
    if (!isRecord(component) || typeof component.type !== 'string') {
      result.unavailableReason ??= 'invalidDefinition';
      continue;
    }
    const type = component.type.toLowerCase();
    if (seen.has(type)) {
      result.unavailableReason ??= 'invalidDefinition';
    }
    seen.add(type);
    result = mergeDefinitions(result, describeComponent(component, type));
  }
  if (!result.body) {
    result.unavailableReason ??= 'invalidDefinition';
  }
  return result;
};

/**
 * Human-readable fallback when providers only supply a technical template name.
 * @param name - Provider template name.
 * @returns A capitalized label with underscores and hyphens replaced by spaces.
 */
export const templateDisplayName = (name: string): string => {
  const label = name.replace(/[_-]+/g, ' ').trim();
  return label ? label[0].toLocaleUpperCase() + label.slice(1) : name;
};

/**
 * Displays provider locale codes using the current UI language.
 * @param code - Meta language code, including underscore-separated regions.
 * @param locale - UI locale.
 * @returns A readable language name or the original unknown code.
 */
export const templateLanguageLabel = (code: string, locale: string): string => {
  try {
    return (
      new Intl.DisplayNames([locale], { type: 'language' }).of(
        code.replace(/_/g, '-')
      ) ?? code
    );
  } catch (error) {
    if (error instanceof RangeError) return code;
    throw error;
  }
};

/**
 * Translates supported Meta categories without mislabeling future categories.
 * @param category - Provider category.
 * @param t - Current translator.
 * @returns A localized category or its original provider label.
 */
export const templateCategoryLabel = (
  category: string,
  t: I18nContextValue['t']
): string => {
  switch (category.toUpperCase()) {
    case 'UTILITY':
      return t('whatsappTemplates.category.utility');
    case 'MARKETING':
      return t('whatsappTemplates.category.marketing');
    case 'AUTHENTICATION':
      return t('whatsappTemplates.category.authentication');
    default:
      return category;
  }
};

/**
 * Renders values literally without replacement-string expansion or recursive substitution.
 * @param text - Approved section text.
 * @param section - Header or body namespace.
 * @param values - User-entered values indexed by component and position.
 * @returns Text with populated placeholders replaced once.
 * @example
 * renderTemplateSection('Hola {{1}}', 'body', { 'body:1': 'María' });
 */
export const renderTemplateSection = (
  text: string,
  section: 'header' | 'body',
  values: TemplateComposerValues
): string =>
  text.replace(
    /\{\{(\d+)\}\}/g,
    (token: string, position: string) =>
      values[`${section}:${position}`] || token
  );

/**
 * Checks required text and URL suffixes without silently changing user input.
 * @param field - Approved positional field descriptor.
 * @param value - User input; omitted values are invalid.
 * @returns Whether required content and component-specific constraints are satisfied.
 */
export const isTemplateFieldValid = (
  field: TemplateComposerField,
  value = ''
): boolean =>
  Boolean(value.trim()) &&
  !/[\r\n\t]| {5,}/.test(value) &&
  (field.section !== 'button' ||
    (!/\s/.test(value) && !/^https?:\/\//i.test(value)));

/**
 * Builds only the editable components, preserving positional and button indices.
 * @param definition - Supported, validated template definition.
 * @param values - Complete values checked with isTemplateFieldValid before submission.
 * @returns Header/body parameters and dynamic URL button parameters, or no components for unsupported formats.
 * @example
 * const definition = describeTemplate(template);
 * const components = buildTemplateComposerComponents(definition, values);
 */
export const buildTemplateComposerComponents = (
  definition: TemplateComposerDefinition,
  values: TemplateComposerValues
): WhatsAppTemplateMessageComponent[] => {
  const components: WhatsAppTemplateMessageComponent[] = [];
  if (definition.unavailableReason) return components;
  for (const section of ['header', 'body'] as const) {
    const fields = definition.fields
      .filter((field) => field.section === section)
      .sort((a, b) => a.position - b.position);
    if (fields.length) {
      components.push({
        type:
          section === 'header'
            ? WhatsAppTemplateHeaderComponentType.HEADER
            : WhatsAppTemplateBodyComponentType.BODY,
        parameters: fields.map((field) => ({
          type: WhatsAppTemplateMessageTextParameterType.TEXT,
          text: values[field.key] ?? '',
        })),
      });
    }
  }
  for (const field of definition.fields.filter(
    (field) => field.section === 'button'
  )) {
    components.push({
      type: WhatsAppTemplateButtonComponentType.BUTTON,
      subType: WhatsAppTemplateMessageButtonSubType.URL,
      index: String(field.buttonIndex),
      parameters: [
        {
          type: WhatsAppTemplateMessageTextParameterType.TEXT,
          text: values[field.key] ?? '',
        },
      ],
    });
  }
  return components;
};

/**
 * Excludes approval examples/status from the draft schema so harmless refreshes retain input.
 * @param template - Refreshed provider template.
 * @returns A stable identity that changes when editable meaning or preview content changes.
 */
export const templateDraftKey = (template: WhatsAppTemplate): string => {
  const definition = describeTemplate(template);
  return JSON.stringify([
    template.id,
    template.name,
    template.language,
    template.parameter_format,
    definition.header,
    definition.body,
    definition.footer,
    definition.fields,
    definition.buttons,
  ]);
};
